import React, { useRef } from 'react'
import {
  SmartWidget,
  Text,
  Flex,
  ResponseContainer,
  toast,
} from '@workwhile/ui'
import { Sparkles } from 'lucide-react'
import {
  ShiftAnalystScreenContextProvider,
  useSummaryScreenContextValue,
} from './context/SummaryScreenContextProvider'
import { MarkdownRenderer } from 'components/MarkdownRenderer/MarkdownRenderer'
import styled from 'styled-components'
import { isAnalysisStatus } from '../../types'

const LoadingText = styled(Text)`
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.intelligence[600]},
    ${({ theme }) => theme.colors.intelligence[300]},
    ${({ theme }) => theme.colors.intelligence[600]}
  );
  background-size: 200% auto;
  animation: text-gradient-animation 1.5s linear infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @keyframes text-gradient-animation {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const AnalysisScreenMain = () => {
  const {
    state: { data, isLoading, isError, errorMessage },
    actions: { refetch },
  } = useSummaryScreenContextValue()

  const lastAnalysisStatus = data.filter(isAnalysisStatus).at(-1)
  const markdownChunks = data.filter((d) => !isAnalysisStatus(d))
  const markdown = markdownChunks.join('')
  const isNotEmpty = !!markdown.trim()

  const markdownRef = useRef<HTMLElement>(null)

  const handleOnCopy = async () => {
    const container = markdownRef.current
    if (!container) return

    const html = container.innerHTML
    const text = container.innerText

    const blobHtml = new Blob([html], { type: 'text/html' })
    const blobText = new Blob([text], { type: 'text/plain' })

    const clipboardItem = new ClipboardItem({
      'text/html': blobHtml,
      'text/plain': blobText,
    })

    try {
      await navigator.clipboard.write([clipboardItem])
    } catch (err) {
      toast.error('Failed to copy to clipboard')
    }
  }

  return (
    <>
      <SmartWidget.Group.Title Icon={Sparkles}>
        Shift Analyst
      </SmartWidget.Group.Title>
      <Text mt={2} color="lightText">
        Smart read on shift health - how much supply you've got, how desirable
        the shift is, what's blocking fill, and more.
      </Text>
      <ResponseContainer
        mt={3}
        refetch={refetch}
        loading={isLoading}
        error={errorMessage}
        disclaimer="AI-generated content may be inaccurate; verify critical information."
        onCopy={isLoading ? undefined : handleOnCopy}
      >
        <Flex maxHeight="50vh" overflow="auto" flexDirection="column">
          {!data.length && isLoading ? (
            <LoadingText fontSize={3} fontWeight="500" mb={2}>
              Thinking
            </LoadingText>
          ) : (
            <>
              {lastAnalysisStatus && isLoading ? (
                <LoadingText fontSize={3} fontWeight="500" mb={2}>
                  {lastAnalysisStatus.status}
                </LoadingText>
              ) : null}
              {isNotEmpty ? (
                <MarkdownRenderer markdown={markdown} ref={markdownRef} />
              ) : null}
            </>
          )}
          {isError ? (
            <Text mb={2} color="lightText">
              Sorry, an error occurred while analyzing this shift. Please try
              again later.
            </Text>
          ) : null}
        </Flex>
      </ResponseContainer>
    </>
  )
}

export const ShiftAnalysisScreen = () => {
  return (
    <ShiftAnalystScreenContextProvider>
      <AnalysisScreenMain />
    </ShiftAnalystScreenContextProvider>
  )
}
